import React, { useState, useEffect, useContext, useMemo } from 'react';
import { ORDER_STATUS } from '@baemingo/order-status-events/dist/types';

import { useBridgeApi, useLogger } from '~Hooks';
import { CURRENCY, SERVICE_TYPE } from '~../__queries__/globalTypes';
import { useDeviceSettingsUniverse, useUpdateDeviceId } from '~Components/DeviceSettingsUniverse';
import { LocalItem } from '~Types';
import { POSOrder, POSPayment } from '~reducers';

const LOCALSTORAGE_KEY = 'ordermanager-storage';

export type AdyenTerminal = {
  IP: string | null;
  POIID: string;
  hasPrinter: boolean;
  useNewPATFlow?: boolean;
};

export type TerminalName = {
  id: string;
  name: string;
};

export type OrderForKivra = {
  order: POSOrder;
  amount: number;
  payment: POSPayment;
};

export interface StorageData {
  adyenTerminals: Array<AdyenTerminal>;
  terminalNames: Array<TerminalName>;
  askToPrint: boolean;
  alwaysPrintReceiptEPOS: boolean;
  manualReceiptPrint: boolean;
  printVoidedItems: boolean;
  useHandheldPrinter: boolean;
  useHandheldBongPrinter: boolean;
  enableTipping: boolean;
  enableBongPrints: boolean;
  bongGroupByStation: boolean;
  bongPrintByStation: boolean;
  bongGroupByCategory: boolean;
  bongShowFloorplan: boolean;
  serviceType: SERVICE_TYPE;
  /**
   * Used in both QSR & Self service kiosks to limit to one service type.
   * It will default to the service type stored in "serviceType"
   */
  hideServiceType: boolean;
  hideCoverageModal: boolean;
  disableStaffTimeout: boolean;
  zoneId: string | null;
  selectedKDSStation: string | null;
  showOrderBottomBar: boolean;
  autoAcceptAppOrders: boolean;
  showOrderEtaBar: boolean;
  showMasonryGrid: boolean;
  showFiveColumns: boolean;
  styleMultiplier: {
    size: number;
    space: number;
  };
  compressedCart: boolean;
  enableNewKDSAppOrders: boolean;
  /**
   * This is used for Brasseriegruppens
   * "Seamless Payments" solution.
   *
   * If the "printQROnCheque" setting is true,
   * we will print a QR code on the receipt that
   * can then be scanned by the BG app and paid for.
   *
   * After the payment is done, the cheque will be closed in the POS
   */
  printQROnCheque: boolean;

  truncateReceiptItemNames: boolean;
  enableServiceTypeDelivery: boolean;

  /**
   * Set after a Device has been assigned. Note: This is *not*
   * "POS devices" that are commonly used for swedish tax authority.
   *
   * This is for the "Device" entity in the backend, which is used
   * for more than just SKV.
   */
  deviceId: string | null;

  /** Generated on first launch, used to identify this device */
  clientId: string | null;

  tripleOrderItemProgress: boolean;
  disableOrderProgress: boolean;
  disableItemProgress: boolean;
  KDSBongOnOrderStatus: ORDER_STATUS | null;
  KDSBongOnProgress: boolean;
  KDSBongOnReady: boolean;
  KDSBongOnItemProgress: boolean;
  KDSFilterOnWholeOrderStatus: boolean;
  KDSProgressOnlyFiltered: boolean;
  KDSShowAllOrderStatuses: boolean;
  KDSPlayNewOrderSound: boolean;
  KDSShowOrderStatusInTSR: boolean;
  KDSGroupItemsByCourseType: boolean;
  KDSCourseOrdersMode: boolean;
  holdFirstCourse: boolean;
  KDSSummaryPanel: boolean;
  KDSOtherStationsFilter: string[];
  KDSHideOrderHeader: boolean;
  KDSLite: boolean;
  filterOrderHistoryByDevice: boolean;
  selectedLanguage: string | null;
  prepTimeMinutes: number;
  bongRemoveArrow: boolean;
  completeAllAppOrdersInstantly: boolean;
  showIncomingAppOrders: boolean;
  autoShowIncomingAppPopup: boolean;
  enableLogRocket: boolean;
  // *** Tax Compliance ***
  // An id that is used to identify the tax compliance settings
  taxComplianceDeviceId: string | null;
  // Name of the tax compliance device, we set this ourselves when creating the device in back office
  // Example: Lobby1, qr1, KDS2 etc
  taxComplianceDeviceName: string | null;
  // The control unit serial number of the tax compliance device
  // Needs to be printed on all receipts
  // Example: PM22500000000000000061191
  taxComplianceControlUnitSerial: string | null;
  // The production number of the tax compliance device
  taxComplianceProductionNumber: string | null;
  // The organization id for the venue / company
  taxComplianceCorporateId: string | null;

  /**
   * Temporary solution to help customers with double print issues.
   * Shows a print ID on the ticket so they can identify duplicates
   */
  BongShowPrintId: boolean;

  /**
   * Automatically retry failed Epson (EpsonSocket) prints up to 3 times.
   * Pings the printer's ePOS endpoint before each retry to verify it's reachable.
   * Waits 5 seconds between attempts. Automatically enables BongShowPrintId to
   * help identify duplicate prints.
   */
  enableEpsonRetry: boolean;

  /**
   * Disables capital letters on the bong, to make them smaller
   */
  BongDisableCapitalLetters: boolean;

  /**
   * If "desired time" is set, this hides all the other fields
   */
  BongHideAllExceptDesiredTime: boolean;

  /**
   * Use the new flow that uses ticket controller
   */
  useNewPrinterFlow: boolean;
  useImagePrinting: boolean;

  /**
   * Disables cash on this device
   */
  disableCash: boolean;

  /**
   * Enable devmode for the POS, used to show extra information or debug purposes
   */
  devMode: boolean;

  /**
   * Orders that are stored for future sync
   */
  storedOrders: Array<{
    order: any;
    amount: any;
    payment: any;
    complianceId?: string | null;
  }>;

  /**
   * Controls whether staff members can view their own sales reports
   * without requiring report permissions.
   */
  staffCanViewOwnReport: boolean;

  /**
   * Controls whether staff without report permissions can view a category breakdown in their sales reports.
   */
  showDetailedSalesBreakdown: boolean;

  /**
   * Show prices on the bong, used to prevent fraud or to differentiate between items
   */
  bongShowPrices: boolean;

  /**
   * Hides items with 0 amount from the cheque and receipt
   */
  hideZeroAmountItemsInCustomerPrints: boolean;

  /**
   * KDS: Variations on items that was not preselected will get marked as green and with a + prefix
   */

  markAddedVariationsGreen: boolean;

  /**
   * Shows a breakdown of 5%, 10%, 15% and 20% tip suggestions on bills printed
   */
  showTipSuggestions: boolean;

  hideDemoMode: boolean;

  /**
   * Controls whether prices are displayed on POS item buttons
   */
  showPricesOnButtons: boolean;

  /**
   * Show images on the POS item buttons
   */
  showImagesOnButtons: boolean;

  /**
   * Ask everytime we try to print a bong if the user wants to print it or not
   */
  askToPrintBong: boolean;

  /**
   * Currency settings for the POS
   */
  currency: {
    symbol: string;
    code: CURRENCY;
  };

  /**
   * Bundle and staged group offers are hidden and only the offer name is shown on receipts instead of all items
   */
  receiptShowOnlyOfferName: boolean;

  /**
   * Sort items alphabetically on bong/kitchen tickets when enabled
   */
  bongSortItemsAlphabetically: boolean;

  /**
   * Plays a sound for new online orders
   */
  onlinePlayNewOrderSound: boolean;

  /**
   * Hide empty tables in the bookings grid (tables with no bookings or cheques)
   */
  hideEmptyTablesInBookings: boolean;

  /**
   * These orders will try to sync to kivra.
   *
   * Orders are added to this array as part of the sync
   * pos order flow, and removed shortly thereafter.
   */
  ordersForKivraSync: OrderForKivra[];

  /**
   * In QSR/Handy mode, replaces the "Continue" button with "Pay"
   * so the payment step is initiated directly from the cart.
   */
  handyDirectPayment: boolean;

  /**
   * Enables creating new invoice customers directly from the invoice payment flow
   */
  enableInvoiceCustomerCreation: boolean;

  /**
   * When enabled, forces the user to select a beeper number before proceeding with payment.
   * If no beeper number has been set on the order, the beeper selector modal will be shown
   * when clicking Pay or Pay With.
   */
  requireBeeperNumber: boolean;
}

/**
 * !!! WHEN ADDING A NEW STORAGE FIELD !!!
 * Also add default data to this constant so that we dont
 * have to do null-checks for missing data everywhere
 */
export const DEFAULT_DATA: StorageData = {
  adyenTerminals: [],
  terminalNames: [],
  askToPrint: false,
  alwaysPrintReceiptEPOS: false,
  manualReceiptPrint: false,
  printVoidedItems: true,
  useHandheldPrinter: true,
  useHandheldBongPrinter: false,
  enableTipping: false,
  enableBongPrints: true,
  bongGroupByStation: false,
  bongPrintByStation: false,
  bongGroupByCategory: false,
  bongShowFloorplan: false,
  serviceType: SERVICE_TYPE.TAKE_AWAY,
  hideServiceType: false,
  hideCoverageModal: false,
  disableStaffTimeout: false,
  zoneId: null,
  selectedKDSStation: null,
  showOrderBottomBar: true,
  printQROnCheque: false,
  autoAcceptAppOrders: false,
  showOrderEtaBar: true,
  markAddedVariationsGreen: false,
  showMasonryGrid: true,
  showFiveColumns: false,
  styleMultiplier: {
    size: 0,
    space: 0,
  },
  compressedCart: false,
  enableNewKDSAppOrders: false,
  truncateReceiptItemNames: true,
  enableServiceTypeDelivery: false,
  deviceId: null,
  clientId: null,
  tripleOrderItemProgress: false,
  disableOrderProgress: false,
  KDSBongOnOrderStatus: null,
  KDSFilterOnWholeOrderStatus: false,
  KDSPlayNewOrderSound: false,
  KDSShowAllOrderStatuses: false,
  filterOrderHistoryByDevice: false,
  selectedLanguage: null,
  KDSBongOnProgress: false,
  KDSBongOnReady: false,
  KDSBongOnItemProgress: false,
  useNewPrinterFlow: false,
  useImagePrinting: false,
  disableItemProgress: false,
  KDSProgressOnlyFiltered: false,
  KDSShowOrderStatusInTSR: false,
  KDSGroupItemsByCourseType: false,
  KDSCourseOrdersMode: false,
  holdFirstCourse: false,
  KDSSummaryPanel: false,
  KDSOtherStationsFilter: [],
  KDSHideOrderHeader: false,
  KDSLite: false,
  prepTimeMinutes: 15,
  bongRemoveArrow: false,
  completeAllAppOrdersInstantly: false,
  showIncomingAppOrders: false,
  autoShowIncomingAppPopup: false,
  enableLogRocket: false,
  taxComplianceDeviceId: null,
  taxComplianceDeviceName: null,
  taxComplianceControlUnitSerial: null,
  taxComplianceProductionNumber: null,
  taxComplianceCorporateId: null,
  BongDisableCapitalLetters: false,
  BongShowPrintId: false,
  enableEpsonRetry: false,
  BongHideAllExceptDesiredTime: false,
  disableCash: false,
  devMode: false,
  storedOrders: [],
  staffCanViewOwnReport: true,
  showDetailedSalesBreakdown: true,
  bongShowPrices: false,
  hideZeroAmountItemsInCustomerPrints: false,
  hideDemoMode: false,
  showTipSuggestions: false,
  showPricesOnButtons: false,
  showImagesOnButtons: false,
  askToPrintBong: false,
  bongSortItemsAlphabetically: false,
  currency: {
    symbol: 'kr',
    code: CURRENCY.SEK,
  },
  receiptShowOnlyOfferName: false,
  onlinePlayNewOrderSound: false,
  hideEmptyTablesInBookings: true,
  ordersForKivraSync: [],
  handyDirectPayment: false,
  enableInvoiceCustomerCreation: false,
  requireBeeperNumber: false,
};

type UpdateFunction = (data: Partial<StorageData>) => void;
type Loading = boolean;

const Context = React.createContext([
  DEFAULT_DATA,
  (data) => {
    // Placeholder
  },
  true,
] as [StorageData, UpdateFunction, Loading]);

export const StorageProvider: React.FunctionComponent = ({ children }) => {
  const [data, setData] = useState<StorageData>(DEFAULT_DATA);
  const [loading, setLoading] = useState(true);
  const deviceSettings = useDeviceSettingsUniverse();
  const updateDeviceId = useUpdateDeviceId();
  const api = useBridgeApi();
  const logger = useLogger('storage');

  const mountLogger = useLogger('mount');

  useEffect(() => {
    mountLogger.info('Mounted: StorageProvider');
    return () => {
      mountLogger.info('Unmounted: StorageProvider');
    };
  }, []);

  useEffect(() => {
    if (window.hasNativeWrapper) {
      // If we are using a native wrapper, use native storage
      api
        .getStorageData()
        .then((newData) => {
          setData({
            ...DEFAULT_DATA,
            ...newData,
          });
          setLoading(false);
        })
        .catch((err) => {
          logger.error('Failed to get storage data', { errorObject: err });
        });
    } else {
      // Otherwise, use computer local storage
      logger.warn('Loading data from localstorage');

      const newData = localStorage.getItem(LOCALSTORAGE_KEY) || '{}';
      const parsedData = JSON.parse(newData);
      setData({
        ...DEFAULT_DATA,
        ...parsedData,
      });
      setLoading(false);
    }
  }, []);

  /**
   * Updates permanent storage with our settings data
   *
   * @param storageData One or more keys from the storage data interface
   */
  function update(storageData: Partial<StorageData>) {
    if (loading) {
      logger.error('Should not update storage while loading');
    }

    setData((_data) => {
      const newData = {
        ..._data,
        ...storageData,
      };

      if (window.hasNativeWrapper) {
        api.updateStorage(newData);
      } else {
        /* Store data in local storage for development */
        localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(newData));
      }

      return newData;
    });
  }

  /**
   * To prevent a circular dependency, we have to "manually" update the device ID
   * in the device settings universe whenever it changes in storage.
   */
  useEffect(() => {
    updateDeviceId(data.deviceId);
  }, [data.deviceId]);

  /**
   * If this is a managed device, merge the device settings with the
   * storage data to allow for remote control of settings.
   */
  const merged = useMemo(() => {
    if (!deviceSettings) {
      return data;
    }

    /** Ensure no values gets set with undefined (this can happen if a remotely controlled setting gets removed) */
    const remotelyControlledData = Object.entries(deviceSettings?.storage ?? {})
      .filter(([_, v]) => v !== undefined)
      .reduce((acc, [k, v]) => {
        acc[k] = v;
        return acc;
      }, {} as Record<string, unknown>);

    return {
      ...data,
      ...remotelyControlledData,
    };
  }, [data, deviceSettings]);

  return <Context.Provider value={[merged, update, loading]}>{children}</Context.Provider>;
};

export const useStorage = () => {
  return useContext(Context);
};
